const LAST_SEAT_KEY = "portal:lastSeat";
const SWITCH_NOTICE_KEY = "portal:switchNotice";

export const ADMIN_PORTAL_URL = process.env.REACT_APP_ADMIN_PORTAL_URL || "https://admin.wasaa.markets";

// Role identity colours, Wasaa palette: Tenant = primary (navy / azure in dark),
// Partner = --chart-5 violet.
export const ROLE_THEMES = {
  TENANT: {
    name: "Tenant",
    bar: "bg-primary",
    badge: "bg-primary/10 text-primary ring-1 ring-primary/25",
    dot: "bg-chart-1",
    text: "text-primary",
    border: "border-primary",
    gradient: "from-accent",
    divider: "bg-border",
    toast: "border-primary/25 bg-popover text-popover-foreground",
    chooser: "hover:border-primary hover:bg-accent",
  },
  PARTNER: {
    name: "Partner",
    bar: "bg-chart-5",
    badge: "bg-chart-5/10 text-purple-700 ring-1 ring-chart-5/30 dark:text-purple-300",
    dot: "bg-chart-5",
    text: "text-purple-700 dark:text-purple-300",
    border: "border-chart-5",
    gradient: "from-accent",
    divider: "bg-border",
    toast: "border-chart-5/30 bg-popover text-popover-foreground",
    chooser: "hover:border-chart-5 hover:bg-accent",
  },
};

export function roleTheme(kind) {
  return ROLE_THEMES[kind] || ROLE_THEMES.TENANT;
}

export function seatHomePath(kind) {
  return kind === "PARTNER" ? "/portal/overview" : "/portal/dashboard";
}

export function seatOrganisation(seat) {
  if (!seat) return "";
  if (seat.kind === "PARTNER" && seat.partnerName) return `${seat.partnerName} (partner of ${seat.tenantName})`;
  return seat.tenantName || "";
}

export function seatFromPortal(portal) {
  if (!portal?.tenant) return null;
  return {
    kind: portal.kind,
    tenantId: portal.tenant.id,
    tenantName: portal.tenant.name,
    partnerId: portal.partner?.id ?? null,
    partnerName: portal.partner?.name ?? null,
    roleLabel: portal.roleLabel || (portal.kind === "PARTNER" ? "Partner" : "Tenant"),
    canManage: Boolean(portal.canManage),
  };
}

export function seatKey(seat) {
  return `${seat.kind}:${seat.tenantId}:${seat.partnerId || ""}`;
}

export function seatRequest(seat) {
  const body = { tenantId: seat.tenantId, kind: seat.kind };
  if (seat.kind === "PARTNER" && seat.partnerId) body.partnerId = seat.partnerId;
  return body;
}

export function parseSeatParam(value) {
  if (!value) return null;
  const [rawKind, tenantId, partnerId] = String(value).split(":");
  const kind = (rawKind || "").toUpperCase();
  if ((kind !== "TENANT" && kind !== "PARTNER") || !tenantId) return null;
  return { kind, tenantId, partnerId: kind === "PARTNER" && partnerId ? partnerId : null };
}

export function getRememberedSeat() {
  try {
    const raw = window.localStorage.getItem(LAST_SEAT_KEY);
    if (!raw) return null;
    const seat = JSON.parse(raw);
    if (!seat?.tenantId || (seat.kind !== "TENANT" && seat.kind !== "PARTNER")) return null;
    return { kind: seat.kind, tenantId: seat.tenantId, partnerId: seat.partnerId || null };
  } catch {
    return null;
  }
}

export function rememberSeat(seat) {
  try {
    window.localStorage.setItem(
      LAST_SEAT_KEY,
      JSON.stringify({ tenantId: seat.tenantId, kind: seat.kind, partnerId: seat.partnerId || null }),
    );
  } catch {
    return;
  }
}

export function clearRememberedSeat() {
  try {
    window.localStorage.removeItem(LAST_SEAT_KEY);
  } catch {
    return;
  }
}

export function setSwitchNotice(seat) {
  try {
    window.sessionStorage.setItem(
      SWITCH_NOTICE_KEY,
      JSON.stringify({ kind: seat.kind, text: `You are now signed in as ${seat.roleLabel}, ${seatOrganisation(seat)}` }),
    );
  } catch {
    return;
  }
}

export function takeSwitchNotice() {
  try {
    const raw = window.sessionStorage.getItem(SWITCH_NOTICE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(SWITCH_NOTICE_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function groupSeatsByOrganisation(seats) {
  const groups = [];
  const byTenant = new Map();
  (seats || []).forEach((seat) => {
    if (!byTenant.has(seat.tenantId)) {
      const group = { tenantId: seat.tenantId, tenantName: seat.tenantName, seats: [] };
      byTenant.set(seat.tenantId, group);
      groups.push(group);
    }
    byTenant.get(seat.tenantId).seats.push(seat);
  });
  groups.forEach((group) => group.seats.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === "TENANT" ? -1 : 1)));
  return groups;
}
